import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { FileUploader } from "@/shared/components";
import { Button, Input, SearchableSelect, Select, ToggleButton, Toast } from "@/shared/ui";
import { useGetUniversitiesQuery } from "@/shared/api/common";
import { useDocumentSuggest } from "@/shared/hooks";
import { applyOcrFields, mapOcrDegree, resolveUniversityForOcr } from "@/shared/utils";
import type { EducationRecord } from "../../../types/personalSheet";
import { editEducationEntrySchema, editEducationIncompleteEntrySchema } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  records: EducationRecord[];
  initialHasNoEducation?: boolean;
  recordsEditable?: boolean;
  draftAddedRecords?: EducationRecord[];
}

interface EducationEntry {
  id: number | null;
  degree: string;
  university_name: string;
  specialty: string;
  admission_year: string;
  graduation_year: string;
  diploma_number: string;
  is_incomplete: boolean;
  left_from_course: string;
  diplomaExistingUrl: string | null;
  diplomaNewFile: File | null;
  diplomaRemoved: boolean;
  diplomaToken: string | null;
  transcriptExistingUrl: string | null;
  transcriptNewFile: File | null;
  transcriptRemoved: boolean;
  transcriptToken: string | null;
}

type EducationTextField = "degree" | "university_name" | "specialty" | "admission_year" | "graduation_year" | "diploma_number" | "left_from_course";

function makeEmptyEntry(): EducationEntry {
  return {
    id: null,
    degree: "",
    university_name: "",
    specialty: "",
    admission_year: "",
    graduation_year: "",
    diploma_number: "",
    is_incomplete: false,
    left_from_course: "",
    diplomaExistingUrl: null,
    diplomaNewFile: null,
    diplomaRemoved: false,
    diplomaToken: null,
    transcriptExistingUrl: null,
    transcriptNewFile: null,
    transcriptRemoved: false,
    transcriptToken: null,
  };
}

function fromRecord(record: EducationRecord): EducationEntry {
  return {
    id: record.id,
    degree: record.degree,
    university_name: record.university_name,
    specialty: record.specialty,
    admission_year: record.admission_year,
    graduation_year: record.graduation_year,
    diploma_number: record.diploma_number,
    is_incomplete: record.is_incomplete ?? false,
    left_from_course: record.left_from_course ?? "",
    diplomaExistingUrl: record.diploma_file_url,
    diplomaNewFile: null,
    diplomaRemoved: false,
    diplomaToken: record.diploma_file_token ?? null,
    transcriptExistingUrl: record.diploma_transcript_file_url,
    transcriptNewFile: null,
    transcriptRemoved: false,
    transcriptToken: record.diploma_transcript_file_token ?? null,
  };
}

function isFilledEntry(entry: EducationEntry): boolean {
  return (
    entry.degree.trim() !== "" ||
    entry.university_name.trim() !== "" ||
    entry.specialty.trim() !== "" ||
    entry.admission_year.trim() !== "" ||
    entry.graduation_year.trim() !== "" ||
    entry.diploma_number.trim() !== "" ||
    entry.left_from_course.trim() !== ""
  );
}

function toApiEntry(entry: EducationEntry): Record<string, unknown> {
  const api: Record<string, unknown> = {
    degree: entry.degree,
    university_name: entry.university_name,
    specialty: entry.specialty,
    admission_year: entry.admission_year,
    is_incomplete: entry.is_incomplete,
    graduation_year: entry.is_incomplete ? "" : entry.graduation_year,
    diploma_number: entry.is_incomplete ? "" : entry.diploma_number,
    left_from_course: entry.is_incomplete ? entry.left_from_course : "",
  };
  if (entry.id != null) api.id = entry.id;
  if (!entry.is_incomplete) {
    if (entry.diplomaNewFile && entry.diplomaToken) api.diploma_file_token = entry.diplomaToken;
    else if (entry.diplomaRemoved) api.diploma_file = null;
    else if (entry.diplomaToken) api.diploma_file_token = entry.diplomaToken;
    if (entry.transcriptNewFile && entry.transcriptToken) api.diploma_transcript_file_token = entry.transcriptToken;
    else if (entry.transcriptRemoved) api.diploma_transcript_file = null;
    else if (entry.transcriptToken) api.diploma_transcript_file_token = entry.transcriptToken;
  }
  return api;
}

function fileSignature(newFile: File | null, removed: boolean, token: string | null, existingUrl: string | null): string {
  if (newFile) return `new:${token}`;
  if (removed) return "removed";
  return existingUrl ? "keep" : "none";
}

function entriesSignature(entries: EducationEntry[]): string {
  return JSON.stringify(
    entries.filter(isFilledEntry).map((e) => ({
      id: e.id,
      degree: e.degree,
      university_name: e.university_name,
      specialty: e.specialty,
      admission_year: e.admission_year,
      graduation_year: e.graduation_year,
      diploma_number: e.diploma_number,
      is_incomplete: e.is_incomplete,
      left_from_course: e.left_from_course,
      diploma: fileSignature(e.diplomaNewFile, e.diplomaRemoved, e.diplomaToken, e.diplomaExistingUrl),
      transcript: fileSignature(e.transcriptNewFile, e.transcriptRemoved, e.transcriptToken, e.transcriptExistingUrl),
    })),
  );
}

const DEGREE_VALUES = ["bachelor", "master", "phd", "college", "secondary_general"];

const EditEducationSection = forwardRef<SectionFormHandle, Props>(function EditEducationSection(
  { records, initialHasNoEducation, recordsEditable = false, draftAddedRecords },
  ref,
) {
  const { t } = useTranslation("EmployeePortal");
  const { data: universities = [] } = useGetUniversitiesQuery();

  const [hasNoEducation, setHasNoEducation] = useState(initialHasNoEducation ?? false);
  const [newEntries, setNewEntries] = useState<EducationEntry[]>(() =>
    recordsEditable ? records.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? records.map(fromRecord) : []));
  const fileTokenRef = useRef(0);
  const [existingFileChanges, setExistingFileChanges] = useState<
    Record<number, { diploma_file?: File; diploma_transcript_file?: File }>
  >({});
  const [removedExistingFiles, setRemovedExistingFiles] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});
  const [ocrFilled, setOcrFilled] = useState<Map<number, Set<string>>>(new Map());
  const [ocrToast, setOcrToast] = useState(false);
  const [ocrWarning, setOcrWarning] = useState(false);
  const { scan: scanDocument, state: ocrState } = useDocumentSuggest();
  const entriesRef = useRef(newEntries);
  entriesRef.current = newEntries;

  const degreeOptions = DEGREE_VALUES.map((v) => ({
    value: v,
    label: t(`personalSheet.degreeValues.${v}`),
  }));

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function updateEntry(index: number, field: EducationTextField, value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  function setEntryIncomplete(index: number, value: boolean) {
    setNewEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        if (value) {
          return {
            ...entry,
            is_incomplete: true,
            graduation_year: "",
            diploma_number: "",
            diplomaNewFile: null,
            diplomaToken: null,
            diplomaRemoved: false,
            transcriptNewFile: null,
            transcriptToken: null,
            transcriptRemoved: false,
          };
        }
        return { ...entry, is_incomplete: false, left_from_course: "" };
      }),
    );
  }

  function setEntryFile(index: number, which: "diploma" | "transcript", file: File | null) {
    const token = file ? `f${(fileTokenRef.current += 1)}` : null;
    setNewEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        if (which === "diploma") {
          return { ...entry, diplomaNewFile: file, diplomaToken: token, diplomaRemoved: file ? false : entry.diplomaRemoved };
        }
        return { ...entry, transcriptNewFile: file, transcriptToken: token, transcriptRemoved: file ? false : entry.transcriptRemoved };
      }),
    );
  }

  function setEntryFileRemoved(index: number, which: "diploma" | "transcript") {
    setNewEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        if (which === "diploma") return { ...entry, diplomaRemoved: true };
        return { ...entry, transcriptRemoved: true };
      }),
    );
  }

  async function runDiplomaOcr(index: number, file: File) {
    setOcrToast(false);
    setOcrWarning(false);
    const result = await scanDocument(file, "diploma");
    if (result.status === "idle") return;
    const fields = result.fields ?? {};

    const matched =
      (mapOcrDegree(fields.degree) ? 1 : 0) +
      (resolveUniversityForOcr(fields.university_name, universities) ? 1 : 0) +
      (fields.specialty?.trim() ? 1 : 0) +
      (fields.graduation_year?.trim() ? 1 : 0) +
      (fields.diploma_number?.trim() ? 1 : 0);
    if (matched * 2 < 5) {
      setOcrWarning(true);
      return;
    }

    const entry = entriesRef.current[index];
    if (!entry) return;
    const filledKeys: string[] = [];

    if (entry.degree.trim() === "") {
      const degreeValue = mapOcrDegree(fields.degree);
      if (degreeValue) {
        updateEntry(index, "degree", degreeValue);
        filledKeys.push("degree");
      }
    }

    if (entry.university_name.trim() === "") {
      const universityValue = resolveUniversityForOcr(fields.university_name, universities);
      if (universityValue) {
        updateEntry(index, "university_name", universityValue);
        filledKeys.push("university_name");
      }
    }

    const textKeys: ("specialty" | "graduation_year" | "diploma_number")[] = entry.is_incomplete
      ? ["specialty"]
      : ["specialty", "graduation_year", "diploma_number"];
    const textResult = applyOcrFields(
      {
        specialty: entry.specialty,
        graduation_year: entry.graduation_year,
        diploma_number: entry.diploma_number,
      },
      fields,
      textKeys,
    );
    for (const key of textKeys) {
      if (textResult.filled.includes(key)) {
        updateEntry(index, key, textResult.next[key]);
        filledKeys.push(key);
      }
    }

    if (filledKeys.length > 0) {
      setOcrFilled((prev) => {
        const next = new Map(prev);
        next.set(index, new Set(filledKeys));
        return next;
      });
      setOcrToast(true);
    }
  }

  function handleDiplomaFile(index: number, file: File | null) {
    setEntryFile(index, "diploma", file);
    if (file) {
      void runDiplomaOcr(index, file);
    } else {
      setOcrWarning(false);
    }
  }

  function isEntryHighlighted(index: number, field: string): boolean {
    return ocrFilled.get(index)?.has(field) ?? false;
  }

  function handleRemoveExistingFile(key: string) {
    setRemovedExistingFiles((prev) => new Set(prev).add(key));
  }

  function updateExistingFile(recordIndex: number, field: "diploma_file" | "diploma_transcript_file", file: File | null) {
    setExistingFileChanges((prev) => {
      const current = prev[recordIndex] ?? {};
      if (file === null) {
        const next = { ...current };
        delete next[field];
        return { ...prev, [recordIndex]: next };
      }
      return { ...prev, [recordIndex]: { ...current, [field]: file } };
    });
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (hasNoEducation) return [] as unknown as Record<string, unknown>;
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }
        const hasNewFilledEntries = newEntries.some(isFilledEntry);
        const hasExistingFileChanges = Object.keys(existingFileChanges).length > 0;
        if (!hasNewFilledEntries && !hasExistingFileChanges) return null;
        const allRecords = [
          ...records.map((r) => ({
            degree: r.degree,
            university_name: r.university_name,
            specialty: r.specialty,
            admission_year: r.admission_year,
            graduation_year: r.graduation_year,
            diploma_number: r.diploma_number,
            is_incomplete: r.is_incomplete ?? false,
            left_from_course: r.left_from_course ?? "",
          })),
          ...newEntries.filter(isFilledEntry).map((e) => ({
            degree: e.degree,
            university_name: e.university_name,
            specialty: e.specialty,
            admission_year: e.admission_year,
            is_incomplete: e.is_incomplete,
            graduation_year: e.is_incomplete ? "" : e.graduation_year,
            diploma_number: e.is_incomplete ? "" : e.diploma_number,
            left_from_course: e.is_incomplete ? e.left_from_course : "",
          })),
        ];
        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        if (hasNoEducation) return null;
        const files: Record<string, File> = {};
        if (recordsEditable) {
          newEntries.filter(isFilledEntry).forEach((entry) => {
            if (entry.diplomaNewFile && entry.diplomaToken) {
              files[`education.${entry.diplomaToken}.diploma_file`] = entry.diplomaNewFile;
            }
            if (entry.transcriptNewFile && entry.transcriptToken) {
              files[`education.${entry.transcriptToken}.diploma_transcript_file`] = entry.transcriptNewFile;
            }
          });
        } else {
          for (const [idx, changes] of Object.entries(existingFileChanges)) {
            if (changes.diploma_file) files[`education.${idx}.diploma_file`] = changes.diploma_file;
            if (changes.diploma_transcript_file) files[`education.${idx}.diploma_transcript_file`] = changes.diploma_transcript_file;
          }
          newEntries.forEach((entry, i) => {
            const idx = records.length + i;
            if (entry.diplomaNewFile) files[`education.${idx}.diploma_file`] = entry.diplomaNewFile;
            if (entry.transcriptNewFile) files[`education.${idx}.diploma_transcript_file`] = entry.transcriptNewFile;
          });
        }
        return Object.keys(files).length > 0 ? files : null;
      },
      async validate() {
        if (hasNoEducation) {
          setValidationErrors({});
          return true;
        }
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const entryErrors: Record<string, string> = {};
          if (entry.is_incomplete) {
            const result = editEducationIncompleteEntrySchema.safeParse({
              degree: entry.degree,
              university_name: entry.university_name,
              specialty: entry.specialty,
              admission_year: entry.admission_year,
              left_from_course: entry.left_from_course,
            });
            if (!result.success) {
              for (const issue of result.error.issues) {
                entryErrors[issue.path[0] as string] = issue.message;
              }
            }
          } else {
            const result = editEducationEntrySchema.safeParse({
              degree: entry.degree,
              university_name: entry.university_name,
              specialty: entry.specialty,
              admission_year: entry.admission_year,
              graduation_year: entry.graduation_year,
              diploma_number: entry.diploma_number,
            });
            if (!result.success) {
              for (const issue of result.error.issues) {
                entryErrors[issue.path[0] as string] = issue.message;
              }
            }
            const diplomaOk = recordsEditable
              ? (!!entry.diplomaExistingUrl && !entry.diplomaRemoved) || !!entry.diplomaNewFile
              : !!entry.diplomaNewFile || ((!!entry.diplomaToken || !!entry.diplomaExistingUrl) && !entry.diplomaRemoved);
            const transcriptOk = recordsEditable
              ? (!!entry.transcriptExistingUrl && !entry.transcriptRemoved) || !!entry.transcriptNewFile
              : !!entry.transcriptNewFile || ((!!entry.transcriptToken || !!entry.transcriptExistingUrl) && !entry.transcriptRemoved);
            if (!diplomaOk) entryErrors.diploma_file = "personalSheet.validation.required";
            if (!transcriptOk) entryErrors.diploma_transcript_file = "personalSheet.validation.required";
          }
          if (Object.keys(entryErrors).length > 0) {
            allValid = false;
            errors[i] = entryErrors;
          }
        }
        setValidationErrors(errors);
        return allValid;
      },
      isDirty() {
        const flagChanged = hasNoEducation !== (initialHasNoEducation ?? false);
        if (recordsEditable) return flagChanged || entriesSignature(newEntries) !== initialSignatureRef.current;
        return flagChanged || newEntries.some(isFilledEntry) || Object.keys(existingFileChanges).length > 0;
      },
      getFlagChanges() {
        if (hasNoEducation !== (initialHasNoEducation ?? false)) {
          return { has_no_education: hasNoEducation };
        }
        return null;
      },
    };
  });

  const showReadOnly = !recordsEditable;

  return (
    <div className="flex flex-col gap-4">
      {ocrState === "scanning" && (
        <Toast color="grey" text={t("personalSheet.ocr.scanning")} closable={false} autoClose={false} isFullWidth />
      )}
      {ocrToast && (
        <Toast
          key="education-ocr-filled"
          color="positive"
          text={t("personalSheet.ocr.filled")}
          isFullWidth
          onClose={() => setOcrToast(false)}
        />
      )}
      {ocrWarning && (
        <Toast
          key="education-ocr-warning"
          color="notice"
          text={t("personalSheet.ocr.wrongFile.diploma")}
          isFullWidth
          onClose={() => setOcrWarning(false)}
        />
      )}

      <div className="flex items-center justify-between gap-4">
        <span className="text-label-sm content-base-secondary">{t("personalSheet.hasNoEducation")}</span>
        <ToggleButton checked={hasNoEducation} onChange={setHasNoEducation} />
      </div>

      {hasNoEducation ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("personalSheet.noEducationNote")}
        </p>
      ) : (
        <>
          {showReadOnly &&
            records.map((record, index) => (
              <div key={record.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.educationRecord", { number: index + 1 })}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select label={t("personalSheet.fields.degree")} options={degreeOptions} value={record.degree} onChange={() => {}} placeholder="" disabled />
                  <SearchableSelect
                    label={t("personalSheet.fields.universityName")}
                    options={universities}
                    searchKeys={["name"]}
                    displayKey="name"
                    hideSearchIcon
                    value={universities.find((u) => u.name === record.university_name) ?? null}
                    onChange={() => {}}
                    disabled
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label={t("personalSheet.fields.specialty")} placeholder="" value={record.specialty} disabled />
                  {!record.is_incomplete && (
                    <Input label={t("personalSheet.fields.diplomaNumber")} placeholder="" value={record.diploma_number} disabled />
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label={t("personalSheet.fields.admissionYear")} placeholder="" value={record.admission_year} disabled />
                  {record.is_incomplete ? (
                    <Input label={t("personalSheet.fields.leftFromCourse")} placeholder="" value={record.left_from_course ?? ""} disabled />
                  ) : (
                    <Input label={t("personalSheet.fields.graduationYear")} placeholder="" value={record.graduation_year} disabled />
                  )}
                </div>
                {!record.is_incomplete && (
                  <div className="flex flex-col gap-3">
                    <FileUploader
                      label={t("personalSheet.fields.diplomaFile")}
                      existingFileUrl={removedExistingFiles.has(`${index}.diploma`) ? null : record.diploma_file_url}
                      value={existingFileChanges[index]?.diploma_file ?? null}
                      onChange={(file) => updateExistingFile(index, "diploma_file", file)}
                      onRemoveExisting={() => handleRemoveExistingFile(`${index}.diploma`)}
                    />
                    <FileUploader
                      label={t("personalSheet.fields.diplomaTranscriptFile")}
                      existingFileUrl={removedExistingFiles.has(`${index}.transcript`) ? null : record.diploma_transcript_file_url}
                      value={existingFileChanges[index]?.diploma_transcript_file ?? null}
                      onChange={(file) => updateExistingFile(index, "diploma_transcript_file", file)}
                      onRemoveExisting={() => handleRemoveExistingFile(`${index}.transcript`)}
                    />
                  </div>
                )}
              </div>
            ))}

          {newEntries.map((entry, index) => (
            <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-body-bold-md content-base-primary">
                  {t("personalSheet.educationRecord", { number: (showReadOnly ? records.length : 0) + index + 1 })}
                </p>
                <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
                  <Trash size={16} color="currentColor" />
                  {t("personalSheet.removeRecord")}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label={t("personalSheet.fields.degree")}
                  hasRequiredLabel
                  options={degreeOptions}
                  value={entry.degree}
                  onChange={(v) => updateEntry(index, "degree", v ?? "")}
                  placeholder={t("personalSheet.fields.degree")}
                  highlight={isEntryHighlighted(index, "degree")}
                  error={validationErrors[index]?.degree ? t(validationErrors[index].degree) : undefined}
                />
                <SearchableSelect
                  label={t("personalSheet.fields.universityName")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.universityName")}
                  options={universities}
                  searchKeys={["name"]}
                  displayKey="name"
                  hideSearchIcon
                  highlight={isEntryHighlighted(index, "university_name")}
                  value={universities.find((u) => u.name === entry.university_name) ?? null}
                  onChange={(selected) => updateEntry(index, "university_name", selected?.name ?? "")}
                  error={validationErrors[index]?.university_name ? t(validationErrors[index].university_name) : undefined}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t("personalSheet.fields.specialty")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.specialty")}
                  value={entry.specialty}
                  highlight={isEntryHighlighted(index, "specialty")}
                  onChange={(e) => updateEntry(index, "specialty", e.target.value)}
                  error={validationErrors[index]?.specialty ? t(validationErrors[index].specialty) : undefined}
                />
                {!entry.is_incomplete && (
                  <Input
                    label={t("personalSheet.fields.diplomaNumber")}
                    hasRequiredLabel
                    placeholder={t("personalSheet.fields.diplomaNumber")}
                    value={entry.diploma_number}
                    highlight={isEntryHighlighted(index, "diploma_number")}
                    onChange={(e) => updateEntry(index, "diploma_number", e.target.value)}
                    error={validationErrors[index]?.diploma_number ? t(validationErrors[index].diploma_number) : undefined}
                  />
                )}
              </div>

              <div className="flex items-center justify-between gap-4">
                <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.incompleteEducation")}</span>
                <ToggleButton checked={entry.is_incomplete} onChange={(checked) => setEntryIncomplete(index, checked)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t("personalSheet.fields.admissionYear")}
                  hasRequiredLabel
                  placeholder={t("personalSheet.fields.admissionYear")}
                  value={entry.admission_year}
                  onChange={(e) => updateEntry(index, "admission_year", e.target.value)}
                  error={validationErrors[index]?.admission_year ? t(validationErrors[index].admission_year) : undefined}
                />
                {entry.is_incomplete ? (
                  <Input
                    label={t("personalSheet.fields.leftFromCourse")}
                    hasRequiredLabel
                    maxLength={100}
                    placeholder={t("personalSheet.fields.leftFromCourse")}
                    value={entry.left_from_course}
                    onChange={(e) => updateEntry(index, "left_from_course", e.target.value)}
                    error={validationErrors[index]?.left_from_course ? t(validationErrors[index].left_from_course) : undefined}
                  />
                ) : (
                  <Input
                    label={t("personalSheet.fields.graduationYear")}
                    hasRequiredLabel
                    placeholder={t("personalSheet.fields.graduationYear")}
                    value={entry.graduation_year}
                    highlight={isEntryHighlighted(index, "graduation_year")}
                    onChange={(e) => updateEntry(index, "graduation_year", e.target.value)}
                    error={validationErrors[index]?.graduation_year ? t(validationErrors[index].graduation_year) : undefined}
                  />
                )}
              </div>

              {!entry.is_incomplete && (
                <div className="flex flex-col gap-3">
                  <FileUploader
                    label={t("personalSheet.fields.diplomaFile")}
                    hasRequiredLabel
                    existingFileUrl={entry.diplomaRemoved ? null : entry.diplomaExistingUrl}
                    value={entry.diplomaNewFile}
                    onChange={(file) => handleDiplomaFile(index, file)}
                    onRemoveExisting={() => setEntryFileRemoved(index, "diploma")}
                    error={validationErrors[index]?.diploma_file ? t(validationErrors[index].diploma_file) : undefined}
                    namespace="EmployeePortal"
                  />
                  <FileUploader
                    label={t("personalSheet.fields.diplomaTranscriptFile")}
                    hasRequiredLabel
                    existingFileUrl={entry.transcriptRemoved ? null : entry.transcriptExistingUrl}
                    value={entry.transcriptNewFile}
                    onChange={(file) => setEntryFile(index, "transcript", file)}
                    onRemoveExisting={() => setEntryFileRemoved(index, "transcript")}
                    error={validationErrors[index]?.diploma_transcript_file ? t(validationErrors[index].diploma_transcript_file) : undefined}
                    namespace="EmployeePortal"
                  />
                </div>
              )}
            </div>
          ))}

          <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
            <Add size={16} color="currentColor" />
            {t("personalSheet.addRecord")}
          </Button>
        </>
      )}
    </div>
  );
});

export default EditEducationSection;
